import styled from "styled-components";
import { motion, useAnimation, useMotionValueEvent, useScroll } from "framer-motion";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useForm } from "react-hook-form";

const Header = () => {
  const homeMatch = useMatch("/");
  const tvMatch = useMatch("tv");
  const [searchOpen, setSearchOpen] = useState(false);
  const { scrollY } = useScroll();
  const navAnimation = useAnimation();
  const toggleSearch = () => {
    setSearchOpen((prev) => !prev);
  };

  useMotionValueEvent(scrollY, "change", () => {
    if (scrollY.get() > 80) navAnimation.start("scroll");
    else navAnimation.start("top");
  });
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm<IForm>();
  const onValid = (data: IForm) => {
    navigate(`/search?keyword=${data.keyword}`);
  };
  return (
    <Nav
      variants={navVariants}
      animate={navAnimation}
      initial={"top"}
      onClick={() => {
        searchOpen && toggleSearch();
      }}
    >
      <Col>
        <Logo
          variants={logoVariants}
          whileHover="active"
          initial="normal"
          xmlns="http://www.w3.org/2000/svg"
          width="1024"
          height="276.742"
          viewBox="0 0 1024 276.742"
        >
          <motion.path d="M140.803 258.904c-15.404 2.705-31.079 3.516-47.294 5.676l-49.458-144.856v151.073c-15.404 1.621-29.457 3.783-44.051 5.945v-276.742h41.08l56.212 157.021v-157.021h43.511v258.904zm85.131-157.558c16.757 0 42.431-.811 57.835-.811v43.24c-19.189 0-41.619 0-57.835.811v64.322c25.405-1.621 50.809-3.785 76.482-4.596v41.617l-119.724 9.461v-255.39h119.724v43.241h-76.482v58.105zm237.284-58.104h-44.862v198.908c-14.594 0-29.188 0-43.239.539v-199.447h-44.862v-43.242h132.965l-.002 43.242zm70.266 55.132h59.187v43.24h-59.187v98.104h-42.433v-239.718h120.808v43.241h-78.375v55.133zm148.641 103.507c24.594.539 49.456 2.434 73.51 3.783v42.701c-38.646-2.434-77.293-4.863-116.75-5.676v-242.689h43.24v201.881zm109.994 49.457c13.783.812 28.377 1.623 42.43 3.242v-254.58h-42.43v251.338zm231.881-251.338l-54.863 131.615 54.863 145.127c-16.217-2.162-32.432-5.135-48.648-7.838l-31.078-79.994-31.617 73.51c-15.678-2.705-30.812-3.516-46.484-5.678l55.672-126.75-50.269-129.992h46.482l28.377 72.699 30.27-72.699h47.295z" />
        </Logo>
        <Items>
          <Item>
            <Link to={"/"}>
              <ItemContent>Movies</ItemContent> {homeMatch && <Circle layoutId="circle" />}
            </Link>
          </Item>
          <Item>
            <Link to={"tv"}>
              <ItemContent>TV Shows</ItemContent> {tvMatch && <Circle layoutId="circle" />}
            </Link>
          </Item>
        </Items>
      </Col>
      <Col>
        <Search onClick={toggleSearch} onSubmit={handleSubmit(onValid)}>
          <motion.svg
            transition={{ type: "linear" }}
            animate={{ x: searchOpen ? "-15.625rem" : 0 }}
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </motion.svg>
          <Input
            {...register("keyword", { required: true, minLength: 2 })}
            transition={{ type: "linear" }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: searchOpen ? 1 : 0 }}
            placeholder="Search for Movie or TV Show"
            autoComplete="off"
          />
        </Search>
      </Col>
    </Nav>
  );
};

export default Header;

const Nav = styled(motion.nav)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  width: 100%;
  top: 0;
  height: 70px;
  font-size: 16px;
  padding: 20px 60px;
  color: white;
  z-index: 99;
  @media screen and (max-width: 1200px) {
    padding: 20px 30px;
  }
  @media screen and (max-width: 800px) {
    padding: 16px;
  }
`;

const Col = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled(motion.svg)`
  margin-right: 3.125rem;
  width: 5.9375rem;
  height: 1.5625rem;
  fill: ${(props) => props.theme.red};
  path {
    stroke-width: 10px;
    stroke: ${(props) => props.theme.red};
  }
`;

const Items = styled.ul`
  display: flex;
  align-items: center;
`;

const Item = styled.li`
  margin-right: 1.25rem;
  color: ${(props) => props.theme.white.lighter};
  display: flex;
  justify-content: center;
  flex-direction: column;
  position: relative;
`;

const ItemContent = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const Circle = styled(motion.span)`
  position: absolute;
  width: 5px;
  height: 5px;
  background-color: ${(props) => props.theme.red};
  border-radius: 2.5px;
  bottom: -10px;
  left: 0;
  right: 0;
  margin: 0 auto;
`;

const Search = styled.form`
  color: white;
  svg {
    height: 1.875rem;
    cursor: pointer;
  }
  display: flex;
  align-items: center;
  position: relative;
`;

const Input = styled(motion.input)`
  transform-origin: right center;
  position: absolute;
  left: -13.625rem;
  background-color: transparent;
  height: 2.5rem;
  width: 13.625rem;
  padding: 0 10px;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: 500;

  &:focus {
    outline: none;
  }
  &::placeholder {
    color: lightgray;
  }
  @media screen and (max-width: 800px) {
    background-color: rgba(0, 0, 0, 0.8);
    height: 40px;
    border-radius: 5px;
    padding: 10px;
  }
`;

// Variants
const logoVariants = {
  normal: { fillOpacity: 1 },
  active: { fillOpacity: [1, 0, 1, 0, 1], transition: { repeat: Infinity } },
};

const navVariants = {
  top: { backgroundColor: "rgba(0,0,0,0)" },
  scroll: { backgroundColor: "rgba(0,0,0,1)" },
};

interface IForm {
  keyword: string;
}
